import { useEffect, useMemo, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import { adminService } from "../services/adminService";
import { useIndexedUserData } from "../hooks/useIndexedUserData";

// GA4 Property ID
const GA4_PROPERTY_ID = "465798180";

type DateRange = "12m" | "3m" | "1m" | "1w" | "day";

interface MonthlyUserData {
    month: string; // "MM-YYYY"
    newUsers: number;
}

interface DailyUserData {
    date: string; // "YYYY-MM-DD"
    dayLabel: string;
    count: number;
}

interface DailyActiveUsersRequest {
    propertyId: string;
    startDate?: string;
    endDate?: string;
}

interface GaDailyActiveUsersData {
    date: string;
    activeUsers: number;
}

interface DailyActiveUsersResponse {
    success: boolean;
    propertyId: string;
    startDate: string;
    endDate: string;
    data: GaDailyActiveUsersData[];
    totalActiveUsers: number;
}

const RANGE_OPTIONS: { value: DateRange; label: string }[] = [
    { value: "12m", label: "Last 12 Months" },
    { value: "3m", label: "Last 3 Months" },
    { value: "1m", label: "Last Month" },
    { value: "1w", label: "Last Week" },
    { value: "day", label: "Day" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const StatTile: React.FC<{
    label: string;
    value: React.ReactNode;
    caption?: string;
    loading?: boolean;
    comingSoon?: boolean;
}> = ({ label, value, caption, loading, comingSoon }) => (
    <div className="w-full bg-white rounded-xl border p-4 md:p-6 flex flex-col gap-2 justify-between min-h-[120px]">
        <div className="flex items-start justify-between gap-2">
            <div className={`text-2xl md:text-3xl font-bold ${comingSoon ? "text-gray-300" : "text-gray-900"}`}>
                {loading ? (
                    <span className="loading loading-spinner loading-sm"></span>
                ) : comingSoon ? (
                    "—"
                ) : (
                    value
                )}
            </div>
            {comingSoon && (
                <span className="shrink-0 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 uppercase tracking-wide">
                    Coming soon
                </span>
            )}
        </div>
        <div>
            <div className="text-[10px] md:text-xs text-gray-500 uppercase font-medium">{label}</div>
            {caption && <div className="text-[10px] text-gray-400 mt-0.5">{caption}</div>}
        </div>
    </div>
);

const ReportingAnalyticsPage: React.FC = () => {
    const [range, setRange] = useState<DateRange>("12m");
    const [customDay, setCustomDay] = useState<string>(todayISO());

    const [userCount, setUserCount] = useState<number | null>(null);
    const [userCountLoading, setUserCountLoading] = useState(true);

    const [monthlyData, setMonthlyData] = useState<MonthlyUserData[]>([]);
    const [monthlyLoading, setMonthlyLoading] = useState(true);

    const [dailyData, setDailyData] = useState<DailyUserData[]>([]);
    const [dailyLoading, setDailyLoading] = useState(true);

    const [dauData, setDauData] = useState<DailyActiveUsersResponse | null>(null);
    const [dauLoading, setDauLoading] = useState(true);
    const [dauError, setDauError] = useState<string | null>(null);

    const { stats, loading: statsLoading } = useIndexedUserData();

    useEffect(() => {
        document.title = "Reporting Analytics - Peak^ Admin";
    }, []);

    useEffect(() => {
        const fetchUserCount = async () => {
            try {
                const count = await adminService.getUserCount();
                setUserCount(count);
            } catch (error) {
                console.error("Error fetching user count:", error);
            } finally {
                setUserCountLoading(false);
            }
        };
        fetchUserCount();
    }, []);

    useEffect(() => {
        const fetchMonthly = async () => {
            try {
                const docRef = doc(db, "adminStats", "newUsersByMonth");
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const months: MonthlyUserData[] = Object.entries(data)
                        .filter(([key]) => /^\d{2}-\d{4}$/.test(key))
                        .sort(([a], [b]) => {
                            const [mA, yA] = a.split("-");
                            const [mB, yB] = b.split("-");
                            return `${yA}-${mA}`.localeCompare(`${yB}-${mB}`);
                        })
                        .map(([month, value]) => {
                            const v = value as { newUsers?: number };
                            return { month, newUsers: v.newUsers ?? 0 };
                        });
                    setMonthlyData(months);
                }
            } catch (error) {
                console.error("Error fetching monthly user data:", error);
            } finally {
                setMonthlyLoading(false);
            }
        };
        fetchMonthly();
    }, []);

    useEffect(() => {
        const fetchDaily = async () => {
            try {
                const docRef = doc(db, "adminStats", "7DayNewUserTrend");
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const days: DailyUserData[] = (data.days || []).map((d: any) => ({
                        date: d.date,
                        dayLabel: d.dayLabel,
                        count: d.count ?? 0,
                    }));
                    setDailyData(days);
                }
            } catch (error) {
                console.error("Error fetching 7-day new user trend:", error);
            } finally {
                setDailyLoading(false);
            }
        };
        fetchDaily();
    }, []);

    // Resolve the GA4 date range for the selected preset
    const { gaStartDate, gaEndDate } = useMemo(() => {
        if (range === "day") {
            return { gaStartDate: customDay, gaEndDate: customDay };
        }
        const daysBack = range === "1w" ? 6 : range === "1m" ? 29 : range === "3m" ? 89 : 364;
        const start = new Date();
        start.setDate(start.getDate() - daysBack);
        return { gaStartDate: start.toISOString().slice(0, 10), gaEndDate: todayISO() };
    }, [range, customDay]);

    useEffect(() => {
        let cancelled = false;
        const fetchDailyActiveUsers = async () => {
            setDauLoading(true);
            setDauError(null);
            try {
                const getDailyActiveUsers = httpsCallable<DailyActiveUsersRequest, DailyActiveUsersResponse>(
                    functions,
                    "getDailyActiveUsers",
                );
                const result = await getDailyActiveUsers({
                    propertyId: GA4_PROPERTY_ID,
                    startDate: gaStartDate,
                    endDate: gaEndDate,
                });
                if (!cancelled) setDauData(result.data);
            } catch (error) {
                console.error("Error fetching daily active users:", error);
                if (!cancelled) setDauError(error instanceof Error ? error.message : "Failed to load");
            } finally {
                if (!cancelled) setDauLoading(false);
            }
        };
        fetchDailyActiveUsers();
        return () => {
            cancelled = true;
        };
    }, [gaStartDate, gaEndDate]);

    const avgDailyActiveUsers = useMemo(() => {
        if (!dauData || dauData.data.length === 0) return null;
        return Math.round(dauData.totalActiveUsers / dauData.data.length);
    }, [dauData]);

    const rangeLabel = useMemo(() => {
        if (range === "day") {
            return new Date(`${customDay}T00:00:00`).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        }
        return RANGE_OPTIONS.find((r) => r.value === range)?.label ?? "";
    }, [range, customDay]);

    const newUsersLoading = range === "1w" || range === "day" ? dailyLoading : monthlyLoading;

    const newUsersValue = useMemo(() => {
        if (range === "day") {
            const match = dailyData.find((d) => d.date === customDay);
            return match ? match.count : null;
        }
        if (range === "1w") {
            return dailyData.reduce((sum, d) => sum + d.count, 0);
        }
        const monthsToSum = range === "12m" ? 12 : range === "3m" ? 3 : 1;
        return monthlyData.slice(-monthsToSum).reduce((sum, m) => sum + m.newUsers, 0);
    }, [range, customDay, monthlyData, dailyData]);

    const totalSubscribed = stats.premiumUsers + stats.plusUsers + stats.platinumUsers + stats.coachUsers;
    const percentSubscribed = stats.totalUsers > 0 ? (totalSubscribed / stats.totalUsers) * 100 : 0;
    const totalLogs =
        stats.totalDayPosts + stats.totalPosts + stats.totalTrainingLogs + stats.totalActivityLogs + stats.totalSessions;

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6">
                <div className="flex flex-col gap-1">
                    <h1 className="text-xl md:text-2xl font-heading">Reporting Analytics</h1>
                    <p className="text-sm text-gray-500">
                        "New Users" and "Daily Active Users" reflect the selected period below. Subscription, log, and revenue
                        figures reflect current totals across all users.
                    </p>
                </div>

                {/* Date range selector */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center bg-gray-100 rounded-lg p-1 flex-wrap">
                        {RANGE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => setRange(option.value)}
                                className={`px-3 py-1.5 text-xs md:text-sm rounded-md transition-colors ${
                                    range === option.value
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {range === "day" && (
                        <input
                            type="date"
                            value={customDay}
                            max={todayISO()}
                            onChange={(e) => setCustomDay(e.target.value)}
                            className="input input-sm input-bordered text-sm"
                        />
                    )}
                    <span className="text-xs text-gray-400">Showing: {rangeLabel}</span>
                </div>

                {/* Stats grid */}
                <div className="w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    <StatTile
                        label="Registered Users"
                        value={userCount?.toLocaleString() ?? "—"}
                        loading={userCountLoading}
                        caption="Current total"
                    />
                    <StatTile
                        label="New Users"
                        value={newUsersValue !== null ? newUsersValue.toLocaleString() : "No data"}
                        loading={newUsersLoading}
                        caption={rangeLabel}
                    />
                    <StatTile
                        label="Conversion of New Users to Subscription"
                        value="—"
                        comingSoon
                        caption="Requires cohort tracking"
                    />
                    <StatTile
                        label="% of Users Subscribed"
                        value={`${percentSubscribed.toFixed(1)}%`}
                        loading={statsLoading}
                        caption="Current snapshot"
                    />
                    <StatTile
                        label="Daily Active Users"
                        value={dauError ? "—" : avgDailyActiveUsers !== null ? avgDailyActiveUsers.toLocaleString() : "—"}
                        loading={dauLoading}
                        caption={dauError ? dauError : `Avg/day · ${rangeLabel}`}
                    />
                    <StatTile
                        label="Total Logs (All Log Types)"
                        value={totalLogs.toLocaleString()}
                        loading={statsLoading}
                        caption="Current snapshot, all-time"
                    />
                    <StatTile
                        label="Total Subscribed Users"
                        value={totalSubscribed.toLocaleString()}
                        loading={statsLoading}
                        caption="Current snapshot"
                    />
                    <StatTile
                        label="Premium Subscribers"
                        value={stats.premiumUsers.toLocaleString()}
                        loading={statsLoading}
                        caption="Current snapshot"
                    />
                    <StatTile
                        label="Plus Subscribers"
                        value={stats.plusUsers.toLocaleString()}
                        loading={statsLoading}
                        caption="Current snapshot"
                    />
                    <StatTile
                        label="Free Users"
                        value={stats.defaultUsers.toLocaleString()}
                        loading={statsLoading}
                        caption="Current snapshot"
                    />
                    <StatTile
                        label="Elite Subscriptions"
                        value="—"
                        comingSoon
                        caption="Tier not yet defined"
                    />
                    <StatTile
                        label="Subscription Revenue"
                        value="—"
                        comingSoon
                        caption="Requires billing data"
                    />
                </div>
            </div>
        </DashboardWrapper>
    );
};

export default ReportingAnalyticsPage;
